import axios from 'axios';
import { EVENT_SERIES_ENTITY_URL } from 'Constants';
import dayjs from 'dayjs';
import { buildUrl } from 'Util/FetchUtils';
import {
	CustomTicketTypeEntity,
	DaysOfWeekFlags,
	EventLabelEntity,
	ItineraryPlanEntity,
	UploadedImageEntity,
} from 'Models/Entities';
import { priceAdjustmentType } from 'Models/Enums';
import { TicketTypeCapacity } from './EventDetailEntityService';

/**
 * Helper to format time values for the server.
 * Handles Date objects, ISO strings, and "HH:mm:ss" strings.
 * Returns time in "HH:mm:ss" format.
 */
function formatTimeValue(time: string | Date | null | undefined): string {
	if (!time) return '00:00:00';

	if (time instanceof Date) {
		return dayjs(time).format('HH:mm:ss');
	}

	// If it's already a time string like "HH:mm:ss", return as-is
	if (typeof time === 'string' && /^\d{2}:\d{2}(:\d{2})?$/.test(time)) {
		return time.length === 5 ? `${time}:00` : time;
	}

	// If it's an ISO datetime string, parse and extract time
	if (typeof time === 'string') {
		const parsed = dayjs(time);
		if (parsed.isValid()) {
			return parsed.format('HH:mm:ss');
		}
	}

	return '00:00:00';
}

export interface CreateEventSeriesRequest {
	name: string;
	description: string;
	termsAndConditions: string;
	eventLabelId: string | null;
	googleMapUrl?: string;
	address?: string;
	tags: string;
	isFeatured: boolean;
	organisationId: string | null;
	customTicketTypes: {
		singularName: string;
		pluralName: string;
		description: string;
		price: number;
		maxCapacity: number;
		quantity: number;
	}[];
	eventDetails: {
		totalCapacity: number;
		cancelled: boolean;
		hidden: boolean;
		closed: boolean;
		startDate: string | null;
		startTime: string | null;
		endDate: string | null;
		endTime: string | null;
	}[];
	// Occurrence rule fields (optional - for generating multiple occurrences)
	seriesStartDate?: string | null;
	seriesEndDate?: string | null;
	defaultCapacity?: number;
	occurrenceRules?: {
		daysOfWeek: number;
		startTime: string;
		endTime: string;
		adjustmentType: string;
		adjustmentValue: number;
	}[];
}

export interface CreateEventSeriesFormData {
	name: string;
	description: string;
	termsAndConditions: string;
	eventLabelId?: string;
	googleMapUrl?: string;
	address?: string;
	tags: string[];
	isFeatured: boolean;
	organisationId?: string;
	customTicketTypes: {
		singularName: string;
		pluralName: string;
		description?: string;
		price: number;
		maxCapacity: number;
		quantity: number;
	}[];
	// For the default occurrence
	totalCapacity: number;
	hidden: boolean;
	startDate?: Date;
	endDate?: Date;
	// Images
	headerImage?: UploadedImageEntity | null;
	supportingImages?: UploadedImageEntity[];
	// Occurrence rule fields (optional - for generating multiple occurrences)
	seriesStartDate?: Date | null;
	seriesEndDate?: Date | null;
	defaultCapacity?: number;
	occurrenceRules?: {
		daysOfWeek: DaysOfWeekFlags;
		startTime: string | Date;
		endTime: string | Date;
		adjustmentType: priceAdjustmentType;
		adjustmentValue: number;
	}[];
}

/**
 * Creates a new event series with a default event detail occurrence.
 */
export async function CreateEventSeries(
	formData: CreateEventSeriesFormData,
): Promise<{ success: boolean; eventSeriesId: string }> {
	const request: CreateEventSeriesRequest = {
		name: formData.name,
		description: formData.description,
		termsAndConditions: formData.termsAndConditions,
		eventLabelId: formData.eventLabelId ?? null,
		googleMapUrl: formData.googleMapUrl,
		address: formData.address,
		tags: formData.tags.join(', '),
		isFeatured: formData.isFeatured,
		organisationId: formData.organisationId ?? null,
		customTicketTypes: formData.customTicketTypes.map(t => ({
			singularName: t.singularName,
			pluralName: t.pluralName,
			description: t.description ?? '',
			price: t.price,
			maxCapacity: t.maxCapacity,
			quantity: t.quantity,
		})),
		eventDetails: [
			{
				totalCapacity: formData.totalCapacity,
				cancelled: false,
				hidden: formData.hidden,
				closed: false,
				startDate: formData.startDate ? dayjs(formData.startDate).format('YYYY-MM-DD') : null,
				startTime: formData.startDate ? dayjs(formData.startDate).format('HH:mm:ss') : null,
				endDate: formData.endDate ? dayjs(formData.endDate).format('YYYY-MM-DD') : null,
				endTime: formData.endDate ? dayjs(formData.endDate).format('HH:mm:ss') : null,
			},
		],
		// Include occurrence rule data if provided
		seriesStartDate: formData.seriesStartDate ? dayjs(formData.seriesStartDate).format('YYYY-MM-DD') : undefined,
		seriesEndDate: formData.seriesEndDate ? dayjs(formData.seriesEndDate).format('YYYY-MM-DD') : undefined,
		defaultCapacity: formData.defaultCapacity,
		occurrenceRules: formData.occurrenceRules?.map(r => ({
			daysOfWeek: r.daysOfWeek,
			startTime: formatTimeValue(r.startTime),
			endTime: formatTimeValue(r.endTime),
			adjustmentType: r.adjustmentType,
			adjustmentValue: r.adjustmentValue,
		})),
	};

	// Use multipart/form-data if we have images to upload
	// Check for Blob (File extends Blob, so this catches both)
	const headerImageBlob = formData.headerImage?.image;
	const hasHeaderImage = headerImageBlob instanceof Blob;
	const hasSupportingImages = formData.supportingImages?.some(img => img.image instanceof Blob);

	if (hasHeaderImage || hasSupportingImages) {
		const multipartData = new FormData();
		multipartData.append('variables', JSON.stringify(request));

		// Add header image if it's a new upload (Blob/File)
		if (hasHeaderImage && headerImageBlob) {
			multipartData.append('headerImage', headerImageBlob);
		}

		// Add supporting images that are new uploads (Blob/File)
		if (formData.supportingImages) {
			formData.supportingImages.forEach((img, index) => {
				if (img.image instanceof Blob) {
					multipartData.append(`supportingImages[${index}]`, img.image);
				}
			});
		}

		const res = await axios.post(`${EVENT_SERIES_ENTITY_URL}/create-event`, multipartData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		});
		return res.data;
	}

	const res = await axios.post(`${EVENT_SERIES_ENTITY_URL}/create-event`, request);
	return res.data;
}

/**
 * Represents a single event occurrence for selection UI.
 */
export interface EventOccurrence {
	id: string;
	/**
	 * The ferry trip backing this occurrence. Attendees, capacity and communications all hang off the
	 * trip, so anything targeting this occurrence's customers needs it. Absent on occurrences created
	 * before the occurrence rules feature.
	 */
	ferryTripId?: string;
	startDate: string;
	startTime: string;
	endDate: string;
	endTime: string;
	totalCapacity: number;
	remainingCapacity: number;
	isClosed: boolean;
	isSoldOut: boolean;
	isCancelled: boolean;
	isHidden: boolean;
	priceAdjustmentType?: string;
	priceAdjustmentValue?: number;
	ticketTypeCapacities?: TicketTypeCapacity[];
}

/**
 * Represents an EventSeries with its next upcoming occurrence.
 * Used for displaying event cards on the upcoming events page.
 */
export interface EventSeriesWithNextOccurrence {
	// Series data
	id: string;
	name: string;
	description: string;
	googleMapUrl?: string;
	address?: string;
	termsAndConditions: string;
	tags: string;
	isFeatured: boolean;
	eventLabelId?: string;
	eventLabel?: EventLabelEntity;
	customTicketTypes: CustomTicketTypeEntity[];
	fromPrice: number;
	supportingImages: UploadedImageEntity[];
	headerImageId?: string;
	headerImage?: UploadedImageEntity;
	organisationId?: string;
	itineraryPlan?: ItineraryPlanEntity;

	// Next occurrence data
	nextOccurrenceId?: string;
	startDate?: string;
	startTime?: string;
	endDate?: string;
	endTime?: string;
	totalCapacity?: number;
	remainingCapacity?: number;
	cancelled?: boolean;
	hidden?: boolean;
	closed?: boolean;
	hasUpcomingOccurrence: boolean;

	// All upcoming occurrences (only populated when explicitly requested)
	upcomingOccurrences?: EventOccurrence[];
	upcomingOccurrenceCount?: number;
}

/**
 * Response for initial event page data using EventSeries.
 */
export interface EventSeriesInitialPageData {
	allExperiences: EventSeriesWithNextOccurrence[];
	labels: EventLabelEntity[];
}

/**
 * Fetches filtered event series with their next upcoming occurrence.
 */
export async function GetFilteredEventSeries(
	labelId: string | undefined,
	skip: number,
	take: number,
	organisationId?: string,
	spacesAvailable?: number,
	categories?: string[],
	startDate?: Date,
	endDate?: Date,
): Promise<EventSeriesWithNextOccurrence[]> {
	const params: { [key: string]: string } = {
		labelId: labelId ?? 'null',
		skip: skip.toString(),
		take: take.toString(),
		organisationId: organisationId ?? 'null',
	};
	if (spacesAvailable !== undefined) params.spacesAvailable = spacesAvailable.toString();
	if (categories !== undefined) params.categories = categories.join(',');
	if (startDate !== undefined) params.startDate = startDate.toISOString();
	if (endDate !== undefined) params.endDate = endDate.toISOString();

	const res = await axios.get(buildUrl(`${EVENT_SERIES_ENTITY_URL}/get-filtered-series`, params));
	return res.data;
}

/**
 * Fetches initial data for the events page using EventSeries.
 */
export async function GetEventSeriesPageInitialData(
	organisationId?: string,
	spacesAvailable?: number,
	categories?: string[],
	startDate?: Date,
	endDate?: Date,
): Promise<EventSeriesInitialPageData> {
	const params: { [key: string]: string } = {
		organisationId: organisationId ?? 'null',
	};
	if (spacesAvailable !== undefined) params.spacesAvailable = spacesAvailable.toString();
	if (categories !== undefined) params.categories = categories.join(',');
	if (startDate !== undefined) params.startDate = startDate.toISOString();
	if (endDate !== undefined) params.endDate = endDate.toISOString();

	const res = await axios.get(buildUrl(`${EVENT_SERIES_ENTITY_URL}/get-series-page-initial-data`, params));
	return res.data;
}

/**
 * Fetches featured event series with their next upcoming occurrence.
 */
export async function GetFeaturedEventSeries(
	organisationId?: string,
	skip: number = 0,
	take: number = 5,
): Promise<EventSeriesWithNextOccurrence[]> {
	const res = await axios.get(
		buildUrl(`${EVENT_SERIES_ENTITY_URL}/get-featured-series`, {
			organisationId: organisationId ?? 'null',
			skip: skip.toString(),
			take: take.toString(),
		}),
	);
	return res.data;
}

/**
 * Fetches a single event series by ID with its next upcoming occurrence.
 */
export async function GetEventSeriesById(seriesId: string): Promise<EventSeriesWithNextOccurrence> {
	const res = await axios.get(`${EVENT_SERIES_ENTITY_URL}/get-series/${seriesId}`);
	return res.data;
}

/**
 * Fetches an event series by ID with ALL upcoming occurrences.
 * Used for the occurrence selection UI on the event detail page.
 */
export async function GetEventSeriesWithOccurrences(
	seriesId: string,
	maxOccurrences?: number,
): Promise<EventSeriesWithNextOccurrence> {
	const params: { [key: string]: string } = {};
	if (maxOccurrences !== undefined) {
		params.maxOccurrences = maxOccurrences.toString();
	}
	const res = await axios.get(buildUrl(`${EVENT_SERIES_ENTITY_URL}/get-series-with-occurrences/${seriesId}`, params));
	return res.data;
}

/**
 * Deletes an event series and all its occurrences.
 */
export async function DeleteEventSeries(seriesId: string): Promise<void> {
	await axios.delete(`${EVENT_SERIES_ENTITY_URL}/${seriesId}`);
}
